import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { CalendarDays, UtensilsCrossed, Users, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [searchParams] = useSearchParams();

  // Accounts are created by invitation. The public Sign Up form only appears
  // on an empty install (somebody has to become the first admin) or when the
  // backend was deliberately set to allow self-signup.
  const [selfSignup, setSelfSignup] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/setup-status`)
      .then((res) => setSelfSignup(!!res.data.self_signup))
      .catch(() => setSelfSignup(false));
  }, []);

  // The Google callback sends people back here with a reason when it refuses.
  useEffect(() => {
    const err = searchParams.get("error");
    if (err === "not_invited") {
      toast.error("That Google account hasn't been invited. Ask an admin to send you an invitation.");
    } else if (err === "google_auth_failed") {
      toast.error("Google sign-in didn't complete. Please try again.");
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, regForm);
      login(res.data.user, res.data.token);
      toast.success("Account created successfully!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      login(res.data.user, res.data.token);
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Our backend owns the Google OAuth flow and redirects back with a token.
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left - Hero (light side) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-background border-r border-border">
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(27,101,149,0.10),transparent_70%)]" />
        <div className="absolute -bottom-24 -right-16 h-[320px] w-[320px] rounded-full bg-[radial-gradient(circle,rgba(148,163,184,0.18),transparent_70%)]" />

        <div className="relative z-10 flex h-full w-full flex-col items-center justify-center gap-7 p-12 text-center">
          <img
            src="/iv-logo.png"
            alt="Ismaili Volunteers"
            className="h-[116px] w-[116px] object-contain [filter:drop-shadow(0_10px_26px_rgba(100,116,139,0.35))]"
          />

          <div className="space-y-3">
            <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-none text-foreground">
              IVEM Food Portal
            </h1>
            <p className="mx-auto max-w-sm text-base leading-relaxed text-muted-foreground">
              Organize multi-day meetings, plan meals, and coordinate vendors for your community events.
            </p>
          </div>

          <ul className="w-full max-w-sm space-y-2.5 text-left">
            {[
              { icon: CalendarDays, label: "Multi-Day Scheduling", sub: "Plan events across days in one place" },
              { icon: UtensilsCrossed, label: "Meal Planning", sub: "Track dietary needs and service times" },
              { icon: Users, label: "Vendor Coordination", sub: "Vendors, approvals and reimbursements" },
            ].map(({ icon: Icon, label, sub }) => (
              <li
                key={label}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-sm"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 ring-1 ring-primary/20">
                  <Icon className="h-4 w-4 text-primary" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-tight text-foreground">{label}</span>
                  <span className="block text-xs text-muted-foreground leading-tight mt-0.5">{sub}</span>
                </span>
              </li>
            ))}
          </ul>

          {/* Organisation lockup — set as live text so it stays crisp on any screen */}
          <div className="flex flex-col items-center gap-1 pt-1">
            <p className="font-heading text-xl font-extrabold tracking-tight text-primary leading-none">
              Ismaili Volunteers
            </p>
            <div className="flex w-full items-center gap-2">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Southwest
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Auth Form */}
      <div className="dark flex-1 flex items-center justify-center bg-nav-rail p-6 lg:p-12 text-foreground">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden flex flex-col items-center text-center mb-8">
            <img
              src="/iv-logo.png"
              alt="Ismaili Volunteers"
              className="mb-4 h-16 w-16 rounded-full object-cover ring-2 ring-white/70 shadow-[0_0_28px_rgba(226,232,240,0.35)]"
            />
            <h1 className="font-heading text-3xl font-bold text-foreground">IVEM Food Portal</h1>
            <p className="text-muted-foreground mt-2">Event &amp; Food Management Portal</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            {selfSignup ? (
              <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
              </TabsList>
            ) : (
              <p className="text-xs text-muted-foreground text-center mb-4 leading-relaxed" data-testid="invite-only-note">
                <span className="font-semibold text-foreground">By invitation only.</span>{" "}
                Use the link in your invitation email, or sign in with Google using the
                address you were invited with.
              </p>
            )}

            <TabsContent value="login">
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
                  <CardDescription>Sign in to manage your events</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email" type="email" placeholder="coordinator@example.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        required data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password" type="password" placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                      {loading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or continue with</span></div>
                  </div>

                  <Button
                    variant="outline" className="w-full"
                    onClick={handleGoogleLogin}
                    data-testid="google-login-btn"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    Continue with Google
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="register">
              <Card className="border-border/50 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Create account</CardTitle>
                  <CardDescription>Get started with event management</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reg-name">Full Name</Label>
                      <Input
                        id="reg-name" placeholder="John Doe"
                        value={regForm.name}
                        onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                        required data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email" type="email" placeholder="coordinator@example.com"
                        value={regForm.email}
                        onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                        required data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reg-password">Password</Label>
                      <Input
                        id="reg-password" type="password" placeholder="Create a password"
                        value={regForm.password}
                        onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                        required data-testid="register-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-btn">
                      {loading ? "Creating account..." : "Create Account"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or continue with</span></div>
                  </div>

                  <Button
                    variant="outline" className="w-full"
                    onClick={handleGoogleLogin}
                    data-testid="google-register-btn"
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                    Continue with Google
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
